import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { CarePlanService } from 'app/cm/services/care-plan-service';
import { Toaster } from 'app/shared/lib/toaster';
import { I18N } from 'aurelia-i18n';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { confirmation } from 'app/shared/elements/ess-confirm';
import * as CM from 'app/cm/models/essette-api';
import { EventAggregator } from 'aurelia-event-aggregator';
import { StoreFocusEvent } from 'app/shared/lib/events';


@autoinject()
export class OutcomesGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.string({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public goalId!: string;

    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    public readonly: boolean = true;

    @bindable.boolean({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public includeInactive: boolean = false;

    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    public showRouteLink: boolean = true;

    protected outcomes: Array<CM.CarePlanOutcome> = [];
    protected hasEditOutcomeRoute: boolean = false;

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 10;


    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly events: EventAggregator,
        private readonly service: CarePlanService,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.checkPermissions();
        this.reload();
    }


    ////****************** Events ***************************************************////
    /** Handles the grid's sort event */
    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        await this.reload();
        //Call stop Propagation to prevent parent table from reload and losing sort
        event.stopPropagation();
    }

    protected handleLinkClickCapture(): boolean {
        this.events.publish(new StoreFocusEvent());
        return true;
    }

    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasEditOutcomeRoute = this.accountContext.hasRouteAccess('cm.case.careplan.outcomes.edit');
    }

    public async reload(): Promise<void> {
        await this.loadOutcomes();
    }

    protected buildCreatedByMessage(outcome: CM.CarePlanOutcome): string | null {
        const stamp = outcome.metadata?.created?.timestamp;
        const byUser = outcome.metadata?.created?.byUser?.description;

        if(outcome.metadata?.triggeredBy?.description) {
            const key = 'ui:stamps.triggeredBy.timestamp';
            const source = outcome.metadata.triggeredBy.description;
            return this.i18n.tr(key, { source: source, timestamp: stamp, user: byUser });
        }
        else {
            const key = 'ui:stamps.created.timestamp';
            return this.i18n.tr(key, { timestamp: stamp, user: byUser });
        }
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    public async loadOutcomes(): Promise<void> {
        if(!this.goalId) return;

        const options: Partial<IQueryLoadOptions> = {
            includeInactive: this.includeInactive,
            sorting: this.sorting,
            page: this.$pager?.page || 0,
            top: this.pageSize
        };

        const includes = [
            'metadata.created.byUser'
        ];

        const fields = [
            'description',
            'metadata.created.byUser.description',
            'metadata.created.timestamp'
        ];

        try {
            this.state.beginLoad();
            const response = await this.service.loadOutcomesByGoal(this.goalId, options, includes, fields);
            this.outcomes = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    public async activateOutcome(outcomeId: string, isActive: boolean): Promise<void> {
        if(!isActive) {
            const result = await confirmation.showDeactivate('cm:carePlanOutcome.$');
            if(result.dialogResponse === confirmation.options.CANCEL) { return; }
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.activateCarePlanOutcome(outcomeId, isActive);
            isSuccessful = response.isSuccessful;
            await this.reload();
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.activate('cm:carePlanOutcome.$', isActive, isSuccessful);
            this.state.endSave();
        }
    }
}
